import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GuestManager from './GuestManager';
import GuestForm from './GuestForm';
import GuestProfile from './GuestProfile';
import BulkRsvpEditor from './BulkRsvpEditor';
import { useNotifications } from '../NotificationProvider';
import { useRole } from '../../hooks/useRole';
import { useProject } from '../../contexts/ProjectContext';
import { getGuestById, refreshGuestsFromApi } from '../../services/GuestService';
import { GUEST_SAVE_WARNINGS_EVENT } from '../../services/GuestContactCheckService';
import { GUESTS_PATH, guestPath, matchGuestsRoute } from '../../utils/guestRoutes';
import '../../styles/components/Guests.css';

/** The Guests module: the list, the new-guest form, a guest's page and its edit form, chosen by the URL. */
const GuestsPage: React.FC = () => {
  const { activeProjectId } = useProject();
  const lastProject = useRef(activeProjectId);
  const navigateAway = useNavigate();
  // Another project was picked: its guest numbers mean other people, so no page of the old project stays open.
  useEffect(() => {
    if (lastProject.current !== activeProjectId) {
      lastProject.current = activeProjectId;
      navigateAway(GUESTS_PATH);
    }
  }, [activeProjectId, navigateAway]);
  return <GuestsRoutes key={activeProjectId ?? 'none'} />;
};

const GuestsRoutes: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showWarning } = useNotifications();
  const { isPlanner } = useRole();
  const route = matchGuestsRoute(location.pathname) ?? { kind: 'list' as const };
  const needsGuest = route.kind === 'profile' || route.kind === 'edit';
  const [ready, setReady] = useState(() => !needsGuest || Boolean(getGuestById((route as { ref: string }).ref)));

  // A guest page opened cold (a pasted link) loads the list first, so the page never says "not found" too early.
  useEffect(() => {
    if (!needsGuest || ready) return;
    let cancelled = false;
    refreshGuestsFromApi().catch(() => { /* the page shows its own not-found message */ }).finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, [needsGuest, ready]);

  // A save returns the duplicate phone/email notes with the saved guest: a notice, never an error, and the save stands.
  useEffect(() => {
    const onWarnings = (event: Event) => {
      const detail = (event as CustomEvent<{ guestName: string; messages: string[] }>).detail;
      if (detail?.messages?.length) showWarning(`${detail.guestName}: ${detail.messages.join(' ')} The guest was saved.`);
    };
    window.addEventListener(GUEST_SAVE_WARNINGS_EVENT, onWarnings);
    return () => window.removeEventListener(GUEST_SAVE_WARNINGS_EVENT, onWarnings);
  }, [showWarning]);

  if (route.kind === 'list') return <GuestManager />;
  if (route.kind === 'rsvps') return <div className="gst-page"><BulkRsvpEditor /></div>;

  if (route.kind === 'new' || route.kind === 'edit') {
    if (!isPlanner) {
      return <div className="gst-empty" role="alert"><p>Only planners can add or change guests.</p></div>;
    }
    if (route.kind === 'edit') {
      if (!ready) return <div className="gst-empty" role="status"><p>Loading guest…</p></div>;
      const guest = getGuestById(route.ref);
      if (!guest) return <div className="gst-empty" role="status"><p>This guest could not be found.</p></div>;
      return (
        <div className="gst-page gst-page--form">
          <GuestForm key={guest.id} guest={guest} onCancel={() => navigate(guestPath(guest.id)!)}
            onSaved={saved => { showSuccess(`${saved.name} saved.`); navigate(guestPath(saved.id)!); }} />
        </div>
      );
    }
    return (
      <div className="gst-page gst-page--form">
        <GuestForm onCancel={() => navigate(GUESTS_PATH)}
          onSaved={saved => { showSuccess(`${saved.name} added.`); navigate(guestPath(saved.id)!); }} />
      </div>
    );
  }

  if (!ready) return <div className="gst-empty" role="status"><p>Loading guest…</p></div>;
  return <div className="gst-page"><GuestProfile key={route.ref} guestRef={route.ref} /></div>;
};

export default GuestsPage;
